import { useState } from 'react'
import { useSessao } from '../../../auth/contexto'
import { podeFiscalizar } from '../../../auth/permissoes'
import { Botao } from '../../../components/Botao'
import { AvisoDeErro } from '../../../components/Estados'
import { dataHora } from '../../../lib/datas'
import { TEXTOS } from '../../../lib/glossario'
import { useRevogarInfraestrutura } from '../../../api/queries/fiscalizacaoManual'
import type { ApuracaoNaTela } from '../../../types/chamado'
import { DialogoDeTexto } from './DialogoDeTexto'
import { MedicaoDeInfraestrutura } from './MedicaoDeInfraestrutura'

/**
 * O sexto indicador quando ele ainda não tem número — e o porquê, que muda o que a tela oferece.
 *
 * **Este componente existe por causa de um beco.** O IIE fica "não apurado" por dois motivos
 * diferentes e a tela lia um campo só (`apurado`), então pedia os nove números nos dois casos. Em
 * agosto/2026 a regra que consolida os quatro sub-indicadores (`iie.criterio`) só passa a valer em
 * 09/2026: a fiscal ia atrás dos números com a contratada, registrava, o indicador continuava sem
 * número e o formulário voltava à tela. E não havia como desfazer — `iie_medicao` é append-only por
 * gatilho, e a segunda medição é recusada mandando "retire a atual", diante de um botão que não
 * existia.
 *
 * São três estados, e cada um pede uma coisa diferente:
 *
 * 1. **Sem critério vigente** — explica, com o texto que o SERVIDOR manda, e não pede número
 *    nenhum. A vigência é decisão do dono; o papel da tela é expor a decisão, não contorná-la.
 * 2. **Com medição registrada** — mostra de onde ela veio e oferece a retirada com motivo.
 * 3. **Sem medição e com critério** — o formulário dos nove números.
 *
 * O estado 2 aparece MESMO sem critério vigente: é exatamente quem caiu no beco antes da correção
 * que mais precisa do botão de retirada.
 *
 * @param props o mês e o envelope da apuração
 * @returns o cartão do IIE
 */
export function CartaoDoIie({ competencia, dados }: { competencia: string; dados: ApuracaoNaTela }) {
  const { criterioVigente, impedimentoDoCriterio, medicao } = dados.infraestrutura

  if (medicao) {
    return (
      <MedicaoRegistrada competencia={competencia} medicao={medicao} impedimento={impedimentoDoCriterio} />
    )
  }
  if (!criterioVigente) {
    return <SemCriterio impedimento={impedimentoDoCriterio} />
  }
  return <MedicaoDeInfraestrutura competencia={competencia} sondagem={dados.disponibilidadeSondada} />
}

/**
 * A explicação de por que medir agora não produziria número.
 *
 * Tarja âmbar e não vermelha: não é erro de ninguém, é uma regra que ainda não vigora. E o texto do
 * impedimento vem do servidor, que sabe qual parâmetro falta — duplicá-lo aqui criaria uma segunda
 * versão da frase, que envelheceria no dia em que a vigência mudasse.
 */
function SemCriterio({ impedimento }: { impedimento?: string | null }) {
  const t = TEXTOS.iie.semCriterio
  return (
    <section className="cartao space-y-2 p-4" aria-label={t.titulo}>
      <h2 className="text-base font-semibold text-slate-900">{t.titulo}</h2>
      {impedimento && (
        <p className="max-w-prose rounded border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900">
          {impedimento}
        </p>
      )}
      <p className="max-w-prose text-sm text-slate-600">{t.comoSeguir}</p>
    </section>
  )
}

/** A medição vigente e o botão de retirada — o par que o valor da fatura já tinha. */
function MedicaoRegistrada({
  competencia,
  medicao,
  impedimento,
}: {
  competencia: string
  medicao: NonNullable<ApuracaoNaTela['infraestrutura']['medicao']>
  impedimento?: string | null
}) {
  const t = TEXTOS.iie
  const { usuario } = useSessao()
  const [retirando, setRetirando] = useState(false)
  const revogar = useRevogarInfraestrutura()

  return (
    <section className="cartao space-y-2 p-4" aria-label={t.registrada.titulo}>
      <h2 className="text-base font-semibold text-slate-900">{t.registrada.titulo}</h2>
      <p className="text-sm text-slate-700">{t.registrada.fonte(medicao.fonte)}</p>
      <p className="text-xs text-slate-600">
        {t.registrada.por(medicao.registradaPorNome, dataHora(medicao.registradaEm))}
      </p>

      {/* Quem caiu no beco vê aqui por que o indicador continua sem número, ao lado do botão que
          desfaz o registro. Sem esta frase, a tela mostraria uma medição registrada e um indicador
          sem número, sem ligar as duas coisas. */}
      {impedimento && (
        <p className="max-w-prose rounded border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900">
          {impedimento}
        </p>
      )}

      <p className="max-w-prose text-sm text-slate-600">{t.registrada.ajuda}</p>

      {revogar.isError && <AvisoDeErro erro={revogar.error} />}

      {podeFiscalizar(usuario) && (
        <div>
          <Botao onClick={() => setRetirando(true)} disabled={revogar.isPending}>
            {t.retirar}
          </Botao>
        </div>
      )}

      {retirando && (
        <DialogoDeTexto
          titulo={t.retirar}
          ajuda={t.retirarAjuda}
          rotulo="Por que está sendo retirada"
          aoConfirmar={(motivo) => {
            revogar.mutate({ competencia, motivo })
            setRetirando(false)
          }}
          aoFechar={() => setRetirando(false)}
        />
      )}
    </section>
  )
}
